import { Popover, Slider } from 'antd'
import classNames from 'classnames'
import {
  forwardRef,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { uploadQiNiu } from '~/apis/upload-oss'
import { MyIcon } from '~/components/ui/MyIcon'
import { OssBucket } from '~/graphqls/types'
import Api from '~/libs/paint/api'
import bootstrap from '~/libs/paint/main'

import styles from './Paint.module.css'

const Paint = ({ url }: { url: string }, ref: Ref<any>) => {
  const [zoom, setZoom] = useState(0)
  const paintInterface = useRef<Api>()
  const [currentTool, setCurrentTool] = useState<'brush' | 'erase' | 'select'>()
  const [brushSize, setBrushSize] = useState(16)
  const [eraseSize, setEraseSize] = useState(16)

  useEffect(() => {
    paintInterface.current = bootstrap()
    paintInterface.current.onZoom((n: number) => {
      setZoom(n)
    })
    paintInterface.current.loadBottomImage(url)
    paintInterface.current.selectBrush()
    paintInterface.current.setLineWidth(16)
    setCurrentTool('brush')

    return () => {
      paintInterface.current?.destroy()
    }
  }, [])

  useEffect(() => {
    paintInterface.current?.reset()
    paintInterface.current?.loadBottomImage(url)
    setZoom(0)
  }, [url])

  useEffect(() => {
    paintInterface.current?.zoomAtCenter(zoom)
  }, [zoom])

  useImperativeHandle(ref, () => {
    return {
      save: async () => {
        const blob = (await paintInterface.current?.saveMask()) as BlobPart
        if (!blob) return

        const imgFile = new File([blob], 'mask.png')
        const maskUrl = await uploadQiNiu(
          imgFile,
          undefined,
          OssBucket.TRADE_PROTECT,
          'ai_init_image'
        )

        return [url, maskUrl]
      },

      reset: () => {
        paintInterface.current?.reset()
      },
    }
  })

  const selectBrush = (size: number) => {
    setBrushSize(size)
    setCurrentTool('brush')
    paintInterface.current?.selectBrush()
    paintInterface.current?.setLineWidth(size)
  }

  const BrushPopover = (
    <div className="flex items-center justify-between bg-neutral-200 p-1.5 rounded-lg gap-2">
      {[8, 12, 16, 20].map(item => {
        return (
          <div
            key={item}
            className="flex items-center justify-center h-5 cursor-pointer"
            onClick={() => {
              selectBrush(item)
            }}
          >
            <div
              className={classNames('rounded-full bg-neutral-600', {
                '!bg-primary': brushSize === item && currentTool === 'brush',
              })}
              style={{
                width: `${item}px`,
                height: `${item}px`,
              }}
            ></div>
          </div>
        )
      })}
    </div>
  )

  let brushIconName = 'brush'
  if (currentTool === 'brush') {
    if (brushSize === 8) {
      brushIconName = 'brush-1'
    } else if (brushSize === 12) {
      brushIconName = 'brush-2'
    } else if (brushSize === 16) {
      brushIconName = 'brush-3'
    } else if (brushSize === 20) {
      brushIconName = 'brush-4'
    }
  }

  const selectErase = (size: number) => {
    setEraseSize(size)
    setCurrentTool('erase')
    paintInterface.current?.selectErase()
    paintInterface.current?.setLineWidth(size)
  }

  const ErasePopover = (
    <div className="flex items-center justify-between bg-neutral-200 p-1.5 rounded-lg gap-2">
      {[8, 12, 16, 20].map(item => {
        return (
          <div
            key={item}
            className="flex items-center justify-center h-5 cursor-pointer"
            onClick={() => {
              selectErase(item)
            }}
          >
            <div
              className={classNames('rounded-full bg-neutral-600', {
                '!bg-primary': eraseSize === item && currentTool === 'erase',
              })}
              style={{
                width: `${item}px`,
                height: `${item}px`,
              }}
            ></div>
          </div>
        )
      })}
    </div>
  )

  let eraseIconName = 'eraser'
  if (currentTool === 'erase') {
    if (eraseSize === 8) {
      eraseIconName = 'eraser-1'
    } else if (eraseSize === 12) {
      eraseIconName = 'eraser-2'
    } else if (eraseSize === 16) {
      eraseIconName = 'eraser-3'
    } else if (eraseSize === 20) {
      eraseIconName = 'eraser-4'
    }
  }

  return (
    <div className="w-full">
      <div className="relative w-full h-[326px]">
        <canvas
          className="bg-neutral-200 rounded-lg w-full h-full"
          id="canvas_paint"
          width={738}
          height={326}
        ></canvas>
        <div id="canvas_paint_cursor" className="absolute"></div>
      </div>

      <div className="flex items-center justify-center mt-3">
        <div className="flex items-center gap-4">
          <section className="rounded-lg bg-neutral-200 p-1.5 flex items-center">
            <div
              onClick={() => {
                paintInterface.current?.back()
              }}
              className="mr-4 h-5 cursor-pointer"
            >
              <MyIcon
                icon="revoke"
                className="text-[20px] leading-none text-neutral-600"
              />
            </div>

            <div
              onClick={() => {
                paintInterface.current?.reset()
                setZoom(0)
              }}
              className="h-5 cursor-pointer"
            >
              <MyIcon
                icon="reset"
                className="text-[20px] leading-none text-neutral-600"
              />
            </div>
          </section>

          <section className="rounded-lg bg-neutral-200 p-1.5 flex items-center">
            <Popover
              placement="topLeft"
              title={null}
              content={BrushPopover}
              trigger="hover"
              overlayClassName={styles.customPopover}
            >
              <div
                className="mr-4 h-5 cursor-pointer"
                onClick={() => {
                  selectBrush(16)
                }}
              >
                <MyIcon
                  icon={brushIconName}
                  className={classNames(
                    'text-[20px] leading-none text-neutral-600',
                    {
                      '!text-primary': currentTool === 'brush',
                    }
                  )}
                />
              </div>
            </Popover>

            <Popover
              placement="top"
              title={null}
              content={ErasePopover}
              trigger="hover"
              overlayClassName={styles.customPopover}
            >
              <div
                className="mr-4 h-5 cursor-pointer"
                onClick={() => {
                  selectErase(16)
                }}
              >
                <MyIcon
                  icon={eraseIconName}
                  className={classNames(
                    'text-[20px] leading-none text-neutral-600',
                    {
                      '!text-primary': currentTool === 'erase',
                    }
                  )}
                />
              </div>
            </Popover>

            <div
              onClick={() => {
                paintInterface.current?.selectSelect()
                setCurrentTool('select')
              }}
              className="h-5 cursor-pointer"
            >
              <MyIcon
                icon="move"
                className={classNames(
                  'text-[16px] leading-none text-neutral-600',
                  {
                    '!text-primary': currentTool === 'select',
                  }
                )}
              />
            </div>
          </section>

          <section className="rounded-lg bg-neutral-200 p-1.5 flex items-center">
            <div
              onClick={() => {
                setZoom(Math.max(zoom - 50, 100))
              }}
              className="h-5 cursor-pointer"
            >
              <MyIcon
                icon="minus"
                className="text-[20px] leading-none text-neutral-600"
              />
            </div>

            <Slider
              tooltip={{
                open: false,
              }}
              min={100}
              max={1000}
              value={zoom}
              onChange={setZoom}
              className={classNames('w-[76px]', styles.customSlider)}
            />

            <div
              onClick={() => {
                setZoom(Math.min(zoom + 50, 1000))
              }}
              className="h-5 cursor-pointer"
            >
              <MyIcon
                icon="plus"
                className="text-[20px] leading-none text-neutral-600"
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}

export default forwardRef(Paint)
